import { MouseEvent, ReactNode, useCallback, useState } from 'react';
import { faBadgeCheck } from '@fortawesome/pro-solid-svg-icons';
import { faUser } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Typography } from '@design-system/components/typography/typography';
import { PostId, ReactionType } from '../model/post.types';
import { Reply } from '../model/reply.types';
import { formatPostDate } from '../lib/formatPostDate';
import { analytics } from '../lib/analytics';
import { useToggleReplyReaction } from '../api/useToggleReplyReaction';
import { useUpdateReply } from '../api/useUpdateReply';
import { useCommunityContext } from '../lib/communityContext';
import { EditContentForm } from './EditContentForm';
import { MentionText } from './MentionText';
import { PostReactions } from './PostReactions';
import { ReplyActionsMenu } from './ReplyActionsMenu';
import { CommentButton } from './CommentButton';
import { UserProfileModal } from './UserProfileModal';

type Props = {
  reply: Reply;
  postId: PostId;
  onReply?: () => void;
  threadToggle?: ReactNode;
  mentions?: string[];
};

export const ReplyCell = ({
  reply,
  postId,
  onReply,
  threadToggle,
  mentions,
}: Props) => {
  const { communityId, canModerate, canEditContent } = useCommunityContext();
  const toggleReplyReaction = useToggleReplyReaction(communityId);
  const [revealed, setRevealed] = useState(!reply.isSensitive);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [updateReply] = useUpdateReply();
  const { author } = reply;
  const showMenu = canModerate || author.isOwn;
  const canEdit = canEditContent(author.isOwn, author.isCreator);
  const startEditing = useCallback(() => setIsEditing(true), []);

  const saveEdit = (text: string) => {
    setIsEditing(false);
    void updateReply({
      variables: { communityId, replyId: reply.id, text },
      optimisticResponse: {
        updateReply: {
          __typename: 'Reply',
          id: reply.id,
          text,
          edited: reply.edited,
        },
      },
    });
  };

  const openProfile = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setIsProfileOpen(true);
  };

  const identity = (
    <>
      {author.avatarUrl ? (
        <img
          src={author.avatarUrl}
          alt={author.name}
          className="size-10 shrink-0 rounded-full object-cover"
        />
      ) : (
        <div
          aria-label={author.name}
          className="flex size-10 shrink-0 items-center justify-center rounded-full bg-[#D9D9D9] text-white"
        >
          <FontAwesomeIcon icon={faUser} />
        </div>
      )}
      <span className="flex min-w-0 flex-col">
        <span className="flex items-center gap-1.5">
          <Typography
            as="span"
            variant="body"
            size="large"
            className="font-black leading-[140%] text-black"
          >
            {author.name}
          </Typography>
          {author.isCreator && (
            <FontAwesomeIcon
              icon={faBadgeCheck}
              className="text-[#5F1ECC]"
              data-testid="creator-badge"
            />
          )}
        </span>
        <Typography
          as="span"
          variant="body"
          size="small"
          fontWeight="normal"
          className="text-[#2C3548]"
        >
          {formatPostDate(reply.createdAt)}
          {reply.edited && ' (Edited)'}
        </Typography>
      </span>
    </>
  );

  const handleToggleReaction = (type: ReactionType) => {
    const existing = reply.reactions.find((reaction) => reaction.type === type);
    if (existing?.isToggled) {
      analytics.trackReplyReactionRemoved(communityId, reply);
    } else {
      analytics.trackReplyReactionAdded(reply);
    }
    return toggleReplyReaction(reply.id, type);
  };

  return (
    <div data-reply-id={reply.id} className="flex w-full flex-col gap-4 p-4">
      <header className="flex items-center gap-2.5">
        {canModerate ? (
          <>
            <button
              type="button"
              onClick={openProfile}
              aria-label={`View ${author.name}'s profile`}
              className="flex min-w-0 items-center gap-2.5 text-left"
              data-testid="reply-author-profile-trigger"
            >
              {identity}
            </button>
            <UserProfileModal
              author={author}
              open={isProfileOpen}
              onClose={() => setIsProfileOpen(false)}
            />
          </>
        ) : (
          identity
        )}
        {showMenu && (
          <div className="ml-auto shrink-0">
            <ReplyActionsMenu
              reply={reply}
              postId={postId}
              onEdit={canEdit ? startEditing : undefined}
            />
          </div>
        )}
      </header>
      {revealed ? (
        <>
          {isEditing ? (
            <EditContentForm
              initialText={reply.content}
              onCancel={() => setIsEditing(false)}
              onSave={saveEdit}
            />
          ) : (
            <MentionText
              as="p"
              variant="body"
              size="large"
              fontWeight="normal"
              className="break-words"
              content={reply.content}
              mentions={mentions}
            />
          )}
          <div className="flex items-center justify-between">
            <PostReactions
              reactions={reply.reactions}
              onToggle={handleToggleReaction}
            />
            {onReply && <CommentButton label="Reply" onClick={onReply} />}
          </div>
          {threadToggle}
        </>
      ) : (
        <button
          type="button"
          onClick={() => setRevealed(true)}
          className="self-start text-sm text-[#5F1ECC]"
        >
          Sensitive content - tap to reveal
        </button>
      )}
    </div>
  );
};
